"use client";

import { useEffect, useState } from "react";
import { initializePaddle } from "@paddle/paddle-js";
import { errorKind } from "@/lib/client-observability";
import type { PaywallPriceFailure } from "@/lib/paywall-wait";
import {
  buildPaddlePreviewRequest,
  getPaddleLandingConfig,
  parsePaddlePricePreview,
  type PaddlePriceBreakdown,
} from "@/lib/paddle-pricing";

const paddleConfig = getPaddleLandingConfig();

export function usePaddlePricing(enabled = true) {
  const [prices, setPrices] = useState<Record<string, PaddlePriceBreakdown>>({});
  const [loading, setLoading] = useState(enabled && Boolean(paddleConfig));
  const [error, setError] = useState<Error | null>(null);
  const [failure, setFailure] = useState<PaywallPriceFailure | null>(null);

  useEffect(() => {
    if (!enabled || !paddleConfig) return;

    let cancelled = false;
    // Стадия отказа для телеметрии: пока Paddle.js не поднялся — init, дальше —
    // превью цен. Отчёт шлёт не хук, а блок тарифов в момент показа (см.
    // `paywall-wait.ts`): так событие одно на показ, а не на загрузку страницы.
    let stage: "init" | "preview" = "init";

    initializePaddle({ token: paddleConfig.token, environment: paddleConfig.environment })
      .then((paddle) => {
        // initializePaddle резолвится в undefined, если скрипт не поднялся.
        if (!paddle) throw new Error("Paddle.js did not initialize.");
        stage = "preview";
        return paddle.PricePreview(buildPaddlePreviewRequest(paddleConfig.priceIds));
      })
      .then((response) => {
        const parsed = parsePaddlePricePreview(response);
        // Пустой ответ — тоже отказ: карточки остаются без цены.
        if (Object.keys(parsed).length === 0) throw new Error("Paddle price preview returned no prices.");
        if (!cancelled) setPrices(parsed);
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setError(reason instanceof Error ? reason : new Error("Paddle price preview failed."));
          setFailure({
            eventType: stage === "init" ? "paddle_init_error" : "paddle_price_preview_error",
            errorKind: errorKind(reason),
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const active = enabled && Boolean(paddleConfig);
  return {
    prices,
    loading,
    error,
    // Отказ для телеметрии: какое событие и с каким errorKind (контракт кабинета).
    failure,
    // Paddle на этой сборке вообще работает (Scriptario и заданы ключи).
    enabled: active,
    // Цены есть и их можно показать: пустой ответ превью — не готовность.
    pricesReady: active && !loading && !error && Object.keys(prices).length > 0,
  };
}
